import { t, Instance } from 'mobx-state-tree';
import type { StoryMediaAttachmentResponse, StoryResponse } from 'we-tree-api-client/v2';

type StoryResponseWithAuthorDisplay = StoryResponse & {
  authorDisplayName?: string;
  effectiveRole?: string;
  canModify?: boolean;
  canDelete?: boolean;
  canManageAccess?: boolean;
};

export const StoryMediaAttachmentModel = t.model('StoryMediaAttachmentModel', {
  id: t.string,
  mediaAssetId: t.string,
  refRole: t.string,
  displayOrder: t.maybeNull(t.number),
  isPrimary: t.boolean,
  originalFilename: t.string,
  mimeType: t.string,
  width: t.maybeNull(t.number),
  height: t.maybeNull(t.number),
  mediaStatus: t.maybeNull(t.string),
  moderationStatus: t.maybeNull(t.string)
});

export const StoryModel = t
  .model('StoryModel', {
    id: t.string,
    familyId: t.string,
    familyMemberId: t.maybeNull(t.string),
    title: t.string,
    content: t.string,
    contentPreview: t.maybeNull(t.string),
    authorId: t.string,
    authorNickname: t.maybeNull(t.string),
    authorDisplayName: t.maybeNull(t.string),
    status: t.string,
    createdAt: t.string,
    updatedAt: t.string,
    attachmentCount: t.optional(t.number, 0),
    mediaAttachments: t.optional(t.array(StoryMediaAttachmentModel), []),
    // Access the API resolved for the signed-in user on this memory. It accounts for permissions
    // granted on the memory itself and on the member it is about, so it can sit above or below
    // the family role; gating on the family role alone hides explicitly granted access.
    effectiveRole: t.maybeNull(t.string),
    canModify: t.optional(t.boolean, false),
    canDelete: t.optional(t.boolean, false),
    canManageAccess: t.optional(t.boolean, false)
  })
  .views((self) => ({
    get excerpt(): string {
      if (self.contentPreview) return self.contentPreview;
      const text = self.content.trim();
      if (text.length <= 200) return text;
      return `${text.slice(0, 200)}…`;
    },
    get isPending(): boolean {
      return self.status === 'pending';
    },
    get authorLabel(): string {
      const display = self.authorDisplayName?.trim();
      if (display) return display;
      const nickname = self.authorNickname?.trim();
      if (nickname) return nickname;
      return 'Unknown author';
    }
  }));

export type StoryType = Instance<typeof StoryModel>;

function toMediaAttachmentModel(response: StoryMediaAttachmentResponse): Instance<typeof StoryMediaAttachmentModel> {
  return StoryMediaAttachmentModel.create({
    id: response.id,
    mediaAssetId: response.mediaAssetId,
    refRole: response.refRole,
    displayOrder: response.displayOrder ?? null,
    isPrimary: response.isPrimary,
    originalFilename: response.originalFilename,
    mimeType: response.mimeType,
    width: response.width ?? null,
    height: response.height ?? null,
    mediaStatus: response.mediaStatus ?? null,
    moderationStatus: response.moderationStatus ?? null
  });
}

export function createStoryModelFromResponse(
  response: StoryResponseWithAuthorDisplay,
  includeFullContent = true
): Instance<typeof StoryModel> {
  const content = includeFullContent ? response.content : (response.contentPreview ?? response.content);
  return StoryModel.create({
    id: response.id,
    familyId: response.familyId,
    familyMemberId: response.familyMemberId ?? null,
    title: response.title,
    content,
    contentPreview: response.contentPreview ?? null,
    authorId: response.authorId,
    authorNickname: response.authorNickname ?? null,
    authorDisplayName: response.authorDisplayName ?? null,
    status: response.status,
    createdAt: response.createdAt,
    updatedAt: response.updatedAt,
    attachmentCount: response.attachmentCount ?? response.mediaAttachments?.length ?? 0,
    mediaAttachments: (response.mediaAttachments ?? []).map(toMediaAttachmentModel),
    effectiveRole: response.effectiveRole ?? null,
    canModify: response.canModify === true,
    canDelete: response.canDelete === true,
    canManageAccess: response.canManageAccess === true
  });
}
